import type { CSSProperties, JSX, ReactNode } from 'react';
import type { StatusKind } from '@/lib/bands';
import { Icon } from './Icon';

const KIND_CLASS: Record<StatusKind, string> = {
  good: 'chip--good',
  warn: 'chip--warn',
  bad: 'chip--bad',
  info: 'chip--info',
  neutral: '',
};

const KIND_ICON: Record<StatusKind, string> = {
  good: 'checkCircle',
  warn: 'warn',
  bad: 'alert',
  info: 'info',
  neutral: 'info',
};

export type ChipTone = StatusKind | 'navy' | 'blue' | 'mock' | 'plain';

const TONE_CLASS: Record<ChipTone, string> = {
  ...KIND_CLASS,
  navy: 'chip--navy',
  blue: 'chip--blue',
  mock: 'chip--mock',
  plain: '',
};

export interface ChipProps {
  readonly tone?: ChipTone | undefined;
  readonly icon?: string | undefined;
  readonly mono?: boolean | undefined;
  readonly className?: string | undefined;
  readonly style?: CSSProperties | undefined;
  readonly children: ReactNode;
}

export function Chip({
  tone = 'plain',
  icon,
  mono = false,
  className,
  style,
  children,
}: ChipProps): JSX.Element {
  const classes = ['chip', TONE_CLASS[tone], mono ? 'mono' : '', className ?? '']
    .filter(Boolean)
    .join(' ');
  return (
    <span className={classes} style={style}>
      {icon !== undefined && <Icon name={icon} />}
      {children}
    </span>
  );
}

/**
 * Status chip: colour is NEVER the only signal.
 *
 * Every status ships an icon and a text label alongside the colour, so the
 * meaning survives greyscale printing and colour-vision deficiency. That is a
 * hard rule in this design system, not a preference.
 */
export function StatusChip({
  kind,
  label,
  className,
}: {
  readonly kind: StatusKind;
  readonly label: string;
  readonly className?: string | undefined;
}): JSX.Element {
  return (
    <Chip tone={kind} icon={KIND_ICON[kind]} {...(className === undefined ? {} : { className })}>
      {label}
    </Chip>
  );
}

/** Toggleable filter chip. Renders a real button for keyboard access. */
export function ChipButton({
  pressed,
  onClick,
  children,
  icon,
}: {
  readonly pressed: boolean;
  readonly onClick: () => void;
  readonly children: ReactNode;
  readonly icon?: string | undefined;
}): JSX.Element {
  return (
    <button type="button" className="chip chip-btn" aria-pressed={pressed} onClick={onClick}>
      {icon !== undefined && <Icon name={icon} />}
      {children}
    </button>
  );
}

/** Small colour dot, for legends and inline series keys. */
export function Dot({
  color,
  style,
}: {
  readonly color: string;
  readonly style?: CSSProperties;
}): JSX.Element {
  return <span className="dot" style={{ background: color, ...style }} />;
}
